/**
 * Main composable for Transaction Processing Dashboard state management
 * Handles data fetching, filtering, real-time updates, and user interactions
 * @module useTransactionDashboard
 * @version 1.0.0
 */

import { useQuasar } from 'quasar'
import { axiosApi } from 'src/CrudManager/Core/Utils/axios'
import { showErrorNotification, showSuccessNotification, showWarningNotification } from 'src/CrudManager/Core/Utils/ToastNotification'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { BulkOperationResponse, DashboardFilters, DashboardOverview, TransactionDashboardData, UseDashboardReturn } from '../types'

/**
 * Main dashboard composable providing comprehensive transaction monitoring capabilities
 * @returns Complete dashboard interface with state management and operations
 * @example
 * ```typescript
 * const {
 *   data,
 *   loading,
 *   filters,
 *   filteredTransactions,
 *   loadData,
 *   retryTransmission,
 *   bulkRetry
 * } = useTransactionDashboard()
 * ```
 */
export function useTransactionDashboard(): UseDashboardReturn {
  const $q = useQuasar()
  const { t } = useI18n()

  // Core reactive state
  const data = ref<TransactionDashboardData | null>(null)
  const loading = ref(false)
  const error = ref<Error | null>(null)
  const lastRefresh = ref<Date>(new Date())

  // Filter state with sensible defaults
  const filters = ref<DashboardFilters>({
    status: undefined,
    transmissionStatus: undefined,
    transmissionType: undefined,
    dateRange: {
      start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Last 30 days
      end: new Date().toISOString().split('T')[0],
    },
    businessPartnerId: undefined,
    search: '',
    hasErrors: undefined,
    sortBy: 'created_at',
    sortOrder: 'desc',
  })

  // Auto-refresh mechanism
  const autoRefreshInterval = ref<NodeJS.Timeout | null>(null)
  const refreshInterval = 30000 // 30 seconds

  /**
   * Computed filtered transactions based on current filter state
   */
  const filteredTransactions = computed(() => {
    if (!data.value?.transactions.data) return []

    let filtered = [...data.value.transactions.data]

    // Apply search filter
    if (filters.value.search) {
      const searchTerm = filters.value.search.toLowerCase()
      filtered = filtered.filter(
        (transaction) =>
          transaction.number?.toLowerCase().includes(searchTerm) ||
          transaction.receiver_billing?.legal_name?.toLowerCase().includes(searchTerm) ||
          transaction.billingEmail?.toLowerCase().includes(searchTerm) ||
          transaction.id.toLowerCase().includes(searchTerm),
      )
    }

    // Apply status filter
    if (filters.value.status?.length) {
      filtered = filtered.filter((transaction) => filters.value.status!.includes(transaction.status))
    }

    // Apply business partner filter
    if (filters.value.businessPartnerId?.length) {
      filtered = filtered.filter((transaction) => filters.value.businessPartnerId!.includes(transaction.receiver_billing?.id || ''))
    }

    // Apply has errors filter
    if (filters.value.hasErrors !== undefined) {
      filtered = filtered.filter((transaction) => {
        const hasFailedTransmissions = transaction.transmissions?.some((t) => t.status === 'failed')
        return filters.value.hasErrors ? hasFailedTransmissions : !hasFailedTransmissions
      })
    }

    return filtered
  })

  /**
   * Computed filtered transmissions based on current filter state
   */
  const filteredTransmissions = computed(() => {
    if (!data.value?.transmissions.data) return []

    let filtered = [...data.value.transmissions.data]

    // Apply transmission status filter
    if (filters.value.transmissionStatus?.length) {
      filtered = filtered.filter((transmission) => filters.value.transmissionStatus!.includes(transmission.status))
    }

    // Apply transmission type filter
    if (filters.value.transmissionType?.length) {
      filtered = filtered.filter((transmission) => filters.value.transmissionType!.includes(transmission.type))
    }

    // Apply date range filter
    if (filters.value.dateRange) {
      const startDate = new Date(filters.value.dateRange.start)
      const endDate = new Date(filters.value.dateRange.end)

      filtered = filtered.filter((transmission) => {
        const transmissionDate = new Date(transmission.created_at)
        return transmissionDate >= startDate && transmissionDate <= endDate
      })
    }

    return filtered
  })

  /**
   * Computed dashboard statistics based on current data
   */
  const dashboardStats = computed((): DashboardOverview => {
    if (!data.value?.overview) {
      return {
        totalTransactions: 0,
        pendingTransmissions: 0,
        failedTransmissions: 0,
        successfulTransmissions: 0,
        avgProcessingTime: 0,
        statusBreakdown: {} as Record<string, number>,
        typeBreakdown: {} as Record<string, number>,
        recentActivity: [],
      }
    }

    return data.value.overview
  })

  /**
   * Load dashboard data from API with error handling
   * @throws {Error} When API request fails
   */
  const loadData = async (): Promise<void> => {
    loading.value = true
    error.value = null

    try {
      const response = await axiosApi.get('/transaction-dashboard/overview', {
        params: {
          ...filters.value,
          per_page: 50, // Reasonable default
        },
      })

      data.value = response.data
      lastRefresh.value = new Date()

      showSuccessNotification(t('composites.TransactionProcessingDashboard.data_refreshed'))
    } catch (err) {
      error.value = err as Error
      console.error('Failed to load dashboard data:', err)

      showErrorNotification(t('composites.TransactionProcessingDashboard.error_loading_data'), err)
    } finally {
      loading.value = false
    }
  }

  /**
   * Update filters and trigger data reload
   * @param newFilters - Partial filter updates to apply
   */
  const updateFilters = (newFilters: Partial<DashboardFilters>): void => {
    filters.value = { ...filters.value, ...newFilters }
    // Note: loadData will be triggered by the watcher
  }

  /**
   * Retry a failed transmission with comprehensive error handling
   * @param transmissionId - ID of transmission to retry
   * @throws {Error} When retry operation fails
   */
  const retryTransmission = async (transmissionId: string): Promise<void> => {
    try {
      await axiosApi.post(`/transaction-dashboard/retry-transmission/${transmissionId}`)

      // Refresh data to show updated status
      await loadData()

      showSuccessNotification(t('composites.TransactionProcessingDashboard.transmission_retry_success'))
    } catch (err) {
      console.error('Failed to retry transmission:', err)

      showErrorNotification(t('composites.TransactionProcessingDashboard.transmission_retry_failed'), err)

      throw err
    }
  }

  /**
   * Retry multiple transmissions in bulk
   * @param transmissionIds - Array of transmission IDs to retry
   * @returns Bulk operation results with success/failure counts
   */
  const bulkRetry = async (transmissionIds: string[]): Promise<BulkOperationResponse> => {
    try {
      const response = await axiosApi.post('/transaction-dashboard/bulk-retry', {
        action: 'retry',
        transmission_ids: transmissionIds,
      })

      const result = response.data as BulkOperationResponse

      // Refresh data to show updated statuses
      await loadData()

      if (result.failed_count > 0) {
        showWarningNotification(t('composites.TransactionProcessingDashboard.bulk_retry_completed', {
          success: result.success_count,
          failed: result.failed_count,
        }))
      } else {
        showSuccessNotification(t('composites.TransactionProcessingDashboard.bulk_retry_completed', {
          success: result.success_count,
          failed: result.failed_count,
        }))
      }

      return result
    } catch (err) {
      console.error('Failed to bulk retry transmissions:', err)

      showErrorNotification(t('composites.TransactionProcessingDashboard.bulk_retry_failed'), err)

      throw err
    }
  }

  /**
   * Manually trigger a specific job type
   * @param jobType - Type of job to trigger (e.g., 'ProcessReadyToInvoiceTransactionsJob')
   * @throws {Error} When job trigger fails
   */
  const triggerJob = async (jobType: string): Promise<void> => {
    try {
      await axiosApi.post('/transaction-dashboard/trigger-job', {
        job_type: jobType,
      })

      showSuccessNotification(t('composites.TransactionProcessingDashboard.job_triggered_success', { jobType }))

      // Refresh data after a brief delay to allow job to process
      setTimeout(loadData, 2000)
    } catch (err) {
      console.error('Failed to trigger job:', err)

      showErrorNotification(t('composites.TransactionProcessingDashboard.job_trigger_failed', { jobType }), err)

      throw err
    }
  }

  /**
   * Export dashboard data in specified format
   * @param format - Export format ('csv' or 'excel')
   * @throws {Error} When export operation fails
   */
  const exportData = async (format: 'csv' | 'excel'): Promise<void> => {
    try {
      const response = await axiosApi.get('/transaction-dashboard/export', {
        params: {
          format,
          ...filters.value,
        },
        responseType: 'blob',
      })

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `transaction-dashboard-${new Date().toISOString().split('T')[0]}.${format}`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)

      showSuccessNotification(t('composites.TransactionProcessingDashboard.export_success'))
    } catch (err) {
      console.error('Failed to export data:', err)

      showErrorNotification(t('composites.TransactionProcessingDashboard.export_failed'), err)

      throw err
    }
  }

  /**
   * Set up auto-refresh mechanism
   */
  const startAutoRefresh = (): void => {
    if (autoRefreshInterval.value) {
      clearInterval(autoRefreshInterval.value)
    }

    autoRefreshInterval.value = setInterval(() => {
      if (!loading.value) {
        loadData()
      }
    }, refreshInterval)
  }

  /**
   * Stop auto-refresh mechanism
   */
  const stopAutoRefresh = (): void => {
    if (autoRefreshInterval.value) {
      clearInterval(autoRefreshInterval.value)
      autoRefreshInterval.value = null
    }
  }

  // Watch for filter changes and reload data
  watch(
    () => filters.value,
    () => {
      if (data.value) {
        loadData()
      }
    },
    { deep: true },
  )

  // Lifecycle hooks
  onMounted(() => {
    loadData()
    startAutoRefresh()
  })

  onUnmounted(() => {
    stopAutoRefresh()
  })

  return {
    // State
    data,
    loading,
    error,
    filters,

    // Computed
    filteredTransactions,
    filteredTransmissions,
    dashboardStats,

    // Methods
    loadData,
    updateFilters,
    retryTransmission,
    bulkRetry,
    triggerJob,
    exportData,
  }
}
